// Copyright (c) Microsoft. All rights reserved.

import React, { Fragment, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { GetResponseOptions, useChat } from '../../libs/hooks/useChat_v2';
import { ChatMessageType } from '../../libs/models/conversations/ChatMessage';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';

import { setSelectedPlugin, setSelectedPlugins } from '../../redux/features/app/pluginsSlice';
import { setSelectedModel } from '../../redux/features/app/aiModelsSlice';

import { Button } from '../../components/Button';
import { Textarea } from '../../components/Textarea';
import { SkillSlashPopover } from '../../components/skill/SkillSlashPopover';
import { Tooltip } from '../../components/Tooltip';
import { Dropdown } from '../../components/Dropdown';
import { Badge, Dialog, Stack } from '../../components/base';

import FileUploader from '../../components/FileUploader';
import Image from '../../components/Image';
import classNames from 'classnames';

import { IFile, IFileAnalyzedCacheResult } from '../../libs/models/conversations/File';
// import { Constants } from '../../Constants';
import { ModelList } from './unit/ModelList';
import { ChatTemplateList } from './unit/ChatTemplateList';

import { getFileIcon, isImageFile, formatFileSize } from '../../libs/common';
import { usePasteImageUpload } from '../../libs/hooks/usePasteImageUpload';
import IconToolActive from '../../assets/etc-icons/icon_tool_active.svg';
import { stopStreaming } from '../../redux/features/conversations/conversationsSlice_v2';
import { ChatModelSelect } from './unit/ChatModelSelect';
import { showAlertToast, showSettingDialog } from '../../redux/features/app/appSlice';
import { AlertType } from '../../libs/models/AlertType';

import { ExternalSource, IPluginPromptData } from '../../libs/models/studio/PluginPromptList';
// import { PluginAPI } from '../../libs/models/app/Plugin';
import { AIModelName } from '../../libs/models/app/AIModel';
import { PluginAPI, WebSearch, webSearchTool } from '../../libs/models/app/Plugin';
import { ActiveSkill } from '../../libs/models/app/Skill';
import { DropdownHeader } from '../../components/DropdownHeader';
import { Icon } from '../../components/Icon';
import AgentInputForm from './AgentInputForm';
import { hasRequiredAgentInputs, substituteAgentInputVariables } from '../../libs/models/app/AgentInput';
import { usePlugin } from '../../libs/hooks/usePlugin';
// MCP 서버 목록은 ToolSelectPopover에 통합됨
// import { ToolList } from './unit/MCPList';
import { ToolSelectPopover } from '../../components/chat/ToolSelectPopover';
import { getConnectorIcon } from '../../libs/common';
// import debug from 'debug';
// const log = debug(Constants.debug.root).extend('chat-input');
type Position = 'top' | 'right' | 'bottom' | 'left';

interface TooltipButtonProps {
  className?: string;
  theme?: string;
  message: ReactNode;
  position?: Position[];
  icon?: string;
  textBlind?: boolean;
  children?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}

export const TooltipButton: React.FC<TooltipButtonProps> = ({
  className = 'option-item',
  theme = 'inverse',
  message = '',
  position = ['bottom', 'right'],
  icon,
  textBlind = true,
  children,
  disabled,
  onClick,
}) => {
  // const [isDesktop, setIsDesktop] = useState(!checkMobileKate() || window.innerWidth <= 768);
  // useEffect(() => {
  //   setIsDesktop(window.innerWidth > 768);
  // }, []);

  return (
    <Tooltip className={className} theme={theme} message={message} position={position}>
      <Button icon={icon} control textBlind={textBlind} onClick={onClick} disabled={disabled}>
        {children}
      </Button>
    </Tooltip>
  );
};

interface ChatInputProps {
  onSubmit: (options: GetResponseOptions) => void;
  fileUpload: (files: File[]) => void;
  fileDelete: (files: IFile) => void;
  uploadedFiles: IFile[];
  scrollToBottom: () => void;
}

// export const ChatInput: React.FC<ChatInputProps> = ({ isDraggingOver, onDragLeave, onSubmit }) => {
export const AgentModeChatInput: React.FC<ChatInputProps> = ({
  onSubmit,
  fileUpload,
  fileDelete,
  uploadedFiles,
  scrollToBottom,
}) => {
  const dispatch = useAppDispatch();
  const chat = useChat();
  // const [isDesktop, setIsDesktop] = useState(!checkMobileKate() || window.innerWidth <= 768);

  // useEffect(() => {
  //   setIsDesktop(window.innerWidth > 768);
  // }, []);
  const isDesktop = useAppSelector((state: RootState) => state.app.viewType == 'Desktop');
  const { user } = useAppSelector((state: RootState) => state.users);
  const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations_v2);
  const { localMcpTools, connectors } = useAppSelector((state: RootState) => state.tools);
  const settingDialogVisible = useAppSelector((state: RootState) => state.app.settingDialogVisible);
  
  const botStatus = useMemo(() => {
    return selectedId === '' ? false : conversations[selectedId].botResponseStatus;
  }, [conversations, selectedId]);

  useEffect(() => {
    // 랜더시, textarea 포커싱 (pc만)
    if (isDesktop && textAreaRef.current) {
      textAreaRef.current.focus();
    }
    setValue('');
    setWebSearchOn(false);
    setWebSearchSource(null);
  }, [selectedId, isDesktop]);

  const handlePluginKeyDown = (event: { key: string; shiftKey: any }) => {
    if (event.key === '@' || (event.shiftKey && event.key === '2')) {
      setIsPluginOpen(true);
    }
  };
  useEffect(() => {

    window.addEventListener('click', handleClickOutside);
    window.addEventListener('keydown', handlePluginKeyDown);

    return () => {
      window.removeEventListener('click', handleClickOutside);
      window.removeEventListener('keydown', handlePluginKeyDown);
    };
  }, []);



  window.setPlugin = (e: IPluginPromptData) => {
    const plugin: PluginAPI = {
      id: e.id ?? '',
      name: e.name ?? '',
      model: e.model ?? 'GPT-4o-mini',
      persona: e.persona ?? '',
      description: e.description ?? '',
      profileUrl: e.profileUrl ?? '',
      tags: e.tags,
      conversationStarters: e.conversationStarters,
      hidden: e.hidden,
      externalSource: e.externalSource,
    };
    if (e.id != null) {
      if (plugin.model) dispatch(setSelectedModel(plugin.model));

      if (e.externalSource === ExternalSource.Aionu) {
        selectAbcLabAgent(plugin);
      } else {
        dispatch(setSelectedPlugins(plugin));
        chat.addPluginStartMessage(selectedId, plugin, user.userId);
      }
    }
  };

  window.setPrompt = (e: IPluginPromptData) => {
    if (e.promptTemplate) {
      //onLoadPrompt(e);
      setValue(e.promptTemplate ?? '');
      dispatch(setSelectedModel(e.model ?? AIModelName.GPT_4O_Mini));
    }
  };

  // const [uploadedFiles, setUploadedFiles] = useState<IFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
  const modalRef = useRef<{ handleKeyDown: (event: React.KeyboardEvent) => void }>(null);
  const skillRef = useRef<{ handleKeyDown: (event: React.KeyboardEvent) => void } | null>(null);
  const optionRef = useRef<HTMLDivElement | null>(null);

  const [value, setValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [activeSkill, setActiveSkill] = useState<ActiveSkill | null>(null);

  // 스킬 목록 redux
  const skills = useAppSelector((state: RootState) => state.tools.skills);
  const availableSkills = useMemo(
    () => skills.filter((s) => s.enabled).map((s) => ({ id: s.id, name: s.name, description: s.description })),
    [skills],
  );

  // 플러그인 팝오버
  const [isPluginOpen, setIsPluginOpen] = useState(false);
  const { plugins, selectedPlugin } = useAppSelector((state: RootState) => state.plugins);

  // 에이전트 공통
  const agent = usePlugin();

  // 웹검색 on/off
  const [webSearchOn, setWebSearchOn] = useState(false);
  const [webSearchSource, setWebSearchSource] = useState<WebSearch | null>(null);
  const [webSearchState, setwebSearchState] = useState(false);


  const changedWebSearcSource = (value: string | number) => {
    if (value == webSearchSource?.value) {
      setWebSearchSource(null);
      setWebSearchOn(false);
    } else {
      dispatch(setSelectedPlugin(null));
      setAgentMode(false);
      setWebSearchSource(webSearchTool[value]);
      setWebSearchOn(true);
    }
  };
  // const changedWebSearchState = (value: boolean) => {
  //   setwebSearchState(value);
  // };

  // 버전 선택 팝오버
  const [isOpen, setIsOpen] = useState(false);
  const { models, selectedModel } = useAppSelector((state: RootState) => state.models);
  const [isDragging, setIsDragging] = useState(false); // 드래그 여부

  // 파일첨부 드롭다운 상태관리
  // const [attachState, setAttachState] = useState(false);
  // const changedAttachState = (state: boolean) => {
  //   setAttachState(state);
  // };

  // [Mobile] 옵션 토글
  const [isOptionVisible, setIsOptionVisible] = useState(false);

  const toggleOption = () => {
    setIsOptionVisible((prevState) => !prevState);
  };

  // 템플릿 드롭다운 상태관리
  const [templateState, setTemplateState] = useState(false);
  const changedTemplateState = (state: boolean) => {
    setTemplateState(state);
  };

  // 에이전트 모드 토글 상태
  const [agentMode, setAgentMode] = useState(false);
  const toggleAgentMode = () => {
    changedAgentMode(!agentMode);
  }

  const changedAgentMode = (state: boolean) => {
    if (state) {
      setNullSelectedPlugin();
      setWebSearchOn(false);
      setWebSearchSource(null);
    }
    setAgentMode(state);
  }

  // 도구 선택 팝오버 상태
  const [isToolSelectPopoverOpen, setIsToolSelectPopoverOpen] = useState(false);
  
  // 활성화된 도구 목록
  // const [enabledConnectors, setEnabledConnectors] = useState<UserConnector[]>([]);
  // 전체 도구 중 needAttention이 있는지 체크용
  // const [hasNeedAttention, setHasNeedAttention] = useState(false);

  // MCP 활성화된 서버 목록 (하나라도 도구가 켜진 서버)
  const enabledLocalMcpServers = useMemo(() => {
    return Object.entries(localMcpTools)
      .filter(([, serverTools]) => serverTools.some(t => t.enabled))
      .map(([serverName]) => serverName);
  }, [localMcpTools]);

  const enabledConnectors = useMemo(()=>{
    console.log('AgentModeChatInput', connectors)
    return connectors.data.filter((connector) => connector.isEnabled);
  },[connectors])

  const hasNeedAttention = useMemo(()=> {
    return connectors.data.some(c => c.needAttention);
  },[connectors])

  const isKmsConnected = useMemo(()=> {
    return connectors.data.some(c => c.connectorType === 'ConfluenceConnector' && c.isEnabled);
  },[connectors])

  const showAttentionBadge = connectors.data.length == 0 ? false: (hasNeedAttention || !isKmsConnected);

  const attentionMessage = !isKmsConnected
    ? 'KMS가 연결되지 않았어요. KMS 도구를 연결해서 KMS 문서를 검색하고 수정해보세요!'
    : 'ⓘ 사용에 문제가 있는 커넥터가 있습니다.';

  const attentionBadgeClass = !isKmsConnected ? 'tool-attention-badge kms-attention-badge' : 'tool-attention-badge';

  // 총 활성화된 도구 개수 (커넥터 + MCP 서버)
  const totalEnabledCount = enabledConnectors.length + enabledLocalMcpServers.length;

  // 통합 아이콘 목록 (커넥터 + MCP 서버 합쳐서 표시)
  const allEnabledIcons = useMemo(() => {
    const icons: Array<{ type: 'connector' | 'mcp'; name: string; id: string; iconUrl?: string }> = [];
    
    // 커넥터 아이콘 추가
    enabledConnectors.forEach((connector) => {
      icons.push({
        type: 'connector',
        name: connector.connectorType?.includes('outlook') ? 'outlook' : 'prompt',
        id: connector.id ?? connector.connectorType ?? 'unknown',
        iconUrl: getConnectorIcon(connector.iconUri)
      });
    });
    
    // MCP 서버 아이콘 추가
    enabledLocalMcpServers.forEach((serverName) => {
      icons.push({
        type: 'mcp',
        name: serverName.slice(0, 1).toUpperCase(),
        id: serverName
      });
    });
    
    return icons;
  }, [enabledConnectors, enabledLocalMcpServers]);

  // const connectorService = new ConnectorService();

  // 활성화된 도구 목록 가져오기
  // const fetchEnabledConnectors = async () => {
  //   try {
  //     const response = await connectorService.getUserConnectors();
  //     if (response?.data) {
  //       const enabled = response.data.filter(c => c.isEnabled);
  //       setEnabledConnectors(enabled);
  //       // 활성화된 도구 중 needAttention이 있는지 체크
  //       setHasNeedAttention(enabled.some(c => c.needAttention));
  //     }
  //   } catch (error) {
  //     console.error('Failed to fetch enabled connectors:', error);
  //   }
  // };
  
  // 컴포넌트 마운트 시 활성화된 도구 가져오기
  useEffect(() => {
    // void fetchEnabledConnectors();
  }, []);
  
  // 팝오버가 닫힐 때 활성화된 도구 새로고침
  useEffect(() => {
    if (!isToolSelectPopoverOpen) {
      // void fetchEnabledConnectors();
    }
  }, [isToolSelectPopoverOpen]);
  
  // 설정 다이얼로그가 닫힐 때 활성화된 도구 새로고침
  useEffect(() => {
    if (!settingDialogVisible) {
      // void fetchEnabledConnectors();
    }
  }, [settingDialogVisible]);

  React.useEffect(() => {
    if (selectedPlugin) changedAgentMode(false);
  }, [selectedPlugin])
  // 채팅 입력중일 때 활성
  // const [isTyping, setIsTyping] = useState(false);

  // 채팅 답변중일 때 활성
  // const [isReplying, setIsReplying] = useState(false);

  const handleSubmit = (value: string) => {
    if (value.trim() === '') {
      return;
    }
    if (agent.agentInputForm.length > 0 && !hasRequiredAgentInputs(agent.agentInputForm, agent.inputValues)) {
      agent.setInputFormVisible(true);
      return;
    }
    if (uploadedFiles.some((file) => !file.analyzeResult)) {
      dispatch(
        showAlertToast({ altMessage: '파일 업로드가 완료된 후 전송해주세요.', altType: AlertType.Info }),
      );
      return;
    }
    const attachFiles = uploadedFiles
      .map((file) => file.analyzeResult)
      .filter((result): result is IFileAnalyzedCacheResult => !!result);
    const additionalProperties: Record<string, object> = {};
    if (selectedPlugin?.externalSource === ExternalSource.Aionu && Object.keys(agent.inputValues).length > 0) {
      additionalProperties['abclab-input'] = agent.inputValues;
    }
    // 스킬 모드 (수동이면 고른 1개, 자동이면 전체 목록)
    const skillList = activeSkill
      ? [{ Name: activeSkill.name, Description: activeSkill.description ?? '' }]
      : availableSkills.map((s) => ({ Name: s.name, Description: s.description }));
    if (window.electron?.isElectron && skillList.length > 0) {
      additionalProperties.agentSkills = skillList as object;
    }
    // 스킬을 직접 선택한 경우 메시지 앞에 태그를 붙여 보낸다
    const messageValue = activeSkill
      ? `<skill-agent>${activeSkill.name}</skill-agent> ${value}`
      : value;
    const options = {
      value: messageValue,
      messageType: ChatMessageType.ChatMessage,
      chatId: selectedId,
      sessionId: selectedId,
      model: selectedModel.model,
      agentId: webSearchOn ? 'Plugin:AiSearch' : selectedPlugin?.id,
      attachFiles: attachFiles,
      webSearchOn: webSearchOn,
      webSearchSource: webSearchSource?.source,
      mcpTools: Object.values(localMcpTools).flat().filter(c => c.enabled),
      agentMode: agentMode,
      ...(Object.keys(additionalProperties).length > 0 && { additionalProperties }),
    };
    const encoder = new TextEncoder();
    const lenght = encoder.encode(JSON.stringify(options.value));
    if (lenght.byteLength > 90000) {
      dispatch(
        showAlertToast({
          altMessage: String('작성한 내용이 너무 길어요. 수정한 후 다시 시도해주세요.'),
          altType: AlertType.Info,
          // id: Constants.app.CONNECTION_ALERT_ID,
        }),
      );
      return;
    }
    setValue('');
    // setUploadedFiles([]);

    // 채팅방이 없으면 채팅방 생성
    //sendMessage(selectedId);
    onSubmit(options);
    // dispatch(editConversationInput({ id: selectedId, newInput: '' }));
  };

  // /skill 팝업이 떠 있는지 (입력값이 /skill 로 시작)
  const isSkillPopoverOpen = /^\/skill\b/i.test(value.trim());

  usePasteImageUpload(textAreaRef, fileUpload);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isPluginOpen && modalRef.current) {
      modalRef.current.handleKeyDown(event); // 에이전트(@) 모달 키 핸들러
    } else if (isSkillPopoverOpen && skillRef.current) {
      skillRef.current.handleKeyDown(event); // 스킬(/skill) 팝업 키 핸들러
    } else if (event.key === 'Enter' && !event.shiftKey && !botStatus) {
      // 한글 입력 중 Enter 는 조합 확정용으로 한 번 더 들어와 전송이 두 번 실행된다.
      if (event.nativeEvent.isComposing) {
        return;
      }
      event.preventDefault();
      handleSubmit(value); // 검색 처리
    }
  };

  const handleClick = (text: string) => {
    setValue(value + text);
  };

  // /skill 에서 스킬 선택
  const handlePickSkill = async (skill: { id: string; name: string; description: string }) => {
    setValue('');
    const md = (await window.electron?.skillGetMd?.(skill.id)) ?? { ok: false };
    const cfg = (await window.electron?.skillGetConfig?.(skill.id)) ?? { ok: false };
    setActiveSkill({
      id: skill.id,
      name: skill.name,
      description: skill.description,
      skillMd: md.ok ? (md.skillMd ?? '') : '',
      config: cfg.ok ? cfg.config : null,
    });
  };

  const changeSelection = (modelName: string) => {
    dispatch(setSelectedModel(modelName));
  };

  const setNullSelectedPlugin = () => {
    dispatch(setSelectedPlugin(null));
    agent.resetInput();
  };

  /**
   * ABC Lab 에이전트 선택
   */
  const selectAbcLabAgent = (pluginData: PluginAPI) => {
    void agent.getAgent(pluginData.id).then((detail: IPluginPromptData | null) => {
      if (!detail) return;
      const fullPlugin: PluginAPI = {
        ...pluginData,
        aiOnUIConnectData: detail.aiOnUIConnectData,
        conversationStarters: detail.conversationStarters ?? pluginData.conversationStarters,
      };
      dispatch(setSelectedPlugins(fullPlugin));

      const hasInputForm = (fullPlugin.aiOnUIConnectData?.userInputForm?.length ?? 0) > 0;
      if (hasInputForm) {
        // 변수 입력 폼이 있으면 시작문구는 변수 입력 후 출력
        agent.setInputFormVisible(true);
        return;
      } else {
        chat.addPluginStartMessage(selectedId, fullPlugin, user.userId, fullPlugin.aiOnUIConnectData?.openingStatement);
      }
    });
  };

  /** ABC Lab 변수 입력 폼 제출 — 최초 제출 시 변수 치환된 시작문구 출력 */
  const handleAgentInputSubmit = (values: Record<string, string>) => {
    const isFirstSubmit = Object.keys(agent.inputValues).length === 0;
    agent.submitInputValues(values);
    if (isFirstSubmit && selectedPlugin) {
      const opening = substituteAgentInputVariables(
        selectedPlugin.aiOnUIConnectData?.openingStatement,
        values,
      );
      chat.addPluginStartMessage(selectedId, selectedPlugin, user.userId, opening);
    }
  };

  const changePluginSelection = (pluginId: string | null) => {
    setWebSearchOn(false);
    agent.resetInput();

    if (!pluginId) {
      dispatch(setSelectedPlugin(null));
    } else {
      const pluginData = plugins[pluginId];
      if (pluginData.model) dispatch(setSelectedModel(pluginData.model));

      if (pluginData.externalSource === ExternalSource.Aionu) {
        selectAbcLabAgent(pluginData);
      } else {
        dispatch(setSelectedPlugin(pluginId));
        chat.addPluginStartMessage(selectedId, pluginData, user.userId);
      }
    }

    // @ 이후의 입력을 제거
    const regex = new RegExp(`@${searchQuery}($)`);
    setValue((prev) => prev.replace(regex, ''));
    setIsPluginOpen(false);
  };

  const togglePluginDropdown = () => {
    setIsPluginOpen(!isPluginOpen);
    setIsOptionVisible(false);
  };

  // 다른곳 눌렀을 때 팝오버 닫기
  const pluginPopoverRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  // const versionPopoverRef = useRef<HTMLDivElement | null>(null);

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node;
    if (
      pluginPopoverRef.current && 
      !pluginPopoverRef.current.contains(target) && 
      (!popoverRef.current || !popoverRef.current.contains(target))
    ) {
      setIsPluginOpen(false);
    }
    if (optionRef.current && !optionRef.current.contains(target)) {
      setIsOptionVisible(false);
    }
    // 24.6.27 model의 팝오버는 dropdown 컴포넌트에 있어서 삭제처리
    // if (versionPopoverRef.current && !versionPopoverRef.current.contains(event.target as Node)) {
    //   setIsOpen(false);
    // }
  };

  const addFiles = () => {
    // FileUploader 내부의 파일 추가 버튼 클릭 시 파일 선택 창 열기
    const fileInput = document.querySelector('.file-uploader input[type="file"]') as HTMLInputElement;
    console.log('[addFiles] fileInput=', fileInput, 'selectedPlugin=', selectedPlugin);

    // 이전 선택값 초기화
    fileInput.value = '';
    fileInput.click();
  };

  const dragIn = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const dragOut = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (
      e.relatedTarget instanceof HTMLElement &&
      // Container에 포함된다면 return
      e.currentTarget.contains(e.relatedTarget)
    ) {
      return;
    }
    setIsDragging(false);
  };

  const stopResponse = () => {
    dispatch(stopStreaming());
  };

  const messageContent = useMemo(
    () => (
      <>
        잔여 질문 수{' '}
        <span className="count-area">
          <b
            className={
              selectedModel.usage?.remainCount
                ? selectedModel.usage.remainCount < 15
                  ? 'txt-error'
                  : 'txt-point'
                : 'txt-error'
            }
          >
            {selectedModel.usage?.remainCount}
          </b>
          /
          <span className="txt-total">
            {selectedModel.usage?.totalCount ? selectedModel.usage.totalCount : '제한 없음'}
          </span>
        </span>
      </>
    ),
    [selectedModel.usage?.remainCount, selectedModel.usage?.totalCount],
  );

  return (
    <div className="chat-input  agent-mode">
      {isPluginOpen && (
        <div className={classNames({ 'dropdown': isDesktop })}>
          <ModelList
            ref={modalRef}
            data={plugins}
            selectedPluginId={selectedPlugin?.id ?? ''}
            searchQuery={searchQuery}
            onSelect={changePluginSelection}
            isOpen={isPluginOpen}
            setIsOpen={setIsPluginOpen}
          />
        </div>
      )}
      {/* /skill 팝업 */}
      <div className={classNames({ 'dropdown': isDesktop })}>
        <SkillSlashPopover
          ref={skillRef}
          inputValue={value}
          onPick={(skill) => {
            void handlePickSkill(skill);
          }}
        />
      </div>
      <div className="chat-attached-list">
        {/* Hidden input for file upload. Only accept .txt and .pdf files for now. */}
        <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} />
        <ul>
          {uploadedFiles.map((file, index) => (
            <li key={index} className="list-item">
              {isImageFile(file.origin.name) ? (
                !file.result ? (
                  <div className="file-preview">
                    <span className="state is-loading" />
                  </div>
                ) : (
                  <React.Fragment>
                    <div className="file-preview">
                      <img src={URL.createObjectURL(file.origin)} alt="" />
                    </div>
                    <Button
                      className="ds-control-close"
                      control
                      textBlind
                      onClick={() => {
                        fileDelete(file);
                      }}
                    >
                      {file.origin.name} 삭제
                    </Button>
                  </React.Fragment>
                )
              ) : (
                <React.Fragment>
                  <div className="file-icon">
                    <Image url={getFileIcon(file.origin.name)} />
                  </div>
                  <div className="file">
                    <strong className="name">{file.origin.name}</strong>
                    <span className="size">{formatFileSize(file.origin.size)}</span>
                    {!file.result ? (
                      <span className="state is-loading">업로드 중</span>
                    ) : (
                      <span className="state is-complete">완료</span>
                    )}
                  </div>
                  <Button
                    className="ds-control-close"
                    control
                    textBlind
                    onClick={() => {
                      fileDelete(file);
                    }}
                  >
                    {file.origin.name} 삭제
                  </Button>
                </React.Fragment>
              )}
            </li>
          ))}
        </ul>
      </div>
      {/* 선택된 플러그인 위치변경 */}
      {(!!activeSkill || !!selectedPlugin || webSearchOn || (!isDesktop && agentMode) || (!isDesktop && totalEnabledCount > 0)) && (
        <div className="set-plugin-wrap">
          {activeSkill && (
            <div className="set-plugin">
              <div className="model-name">
                <span>/{activeSkill.name}</span>
                <Button
                  className="ds-control-close"
                  control
                  textBlind
                  onClick={() => {
                    setActiveSkill(null);
                  }}
                >
                  스킬 끄기
                </Button>
              </div>
            </div>
          )}
          {selectedPlugin && (
            <div className="set-plugin">
              <div className="model-name">
                <Icon name="prompt" />
                <span>{selectedPlugin.name}</span>
                <Button
                  className="ds-control-close"
                  control
                  textBlind
                  onClick={() => {
                    setNullSelectedPlugin();
                  }}
                >
                  에이전트 끄기
                </Button>
              </div>
            </div>
          )}
          {webSearchOn && (
            <div className="set-plugin">
              <div className="model-name">
                <Icon name={webSearchSource?.icon} />
                <span>{webSearchSource?.label}</span>
                <Button
                  className="ds-control-close"
                  control
                  textBlind
                  onClick={() => {
                    changedWebSearcSource(webSearchSource?.value ?? '');
                  }}
                >
                  에이전트 끄기
                </Button>
              </div>
            </div>
          )}
          {!isDesktop && agentMode && (
            <div className="set-plugin">
              <div className="model-name">
                <Icon name='agent-mode' />
                <span>에이전트 모드</span>
                <Button
                  className="ds-control-close"
                  control
                  textBlind
                  onClick={toggleAgentMode}
                >
                  에이전트 끄기
                </Button>
              </div>
            </div>
          )}
          {!isDesktop && totalEnabledCount > 0 && (
            <div className="set-plugin">
              <button 
                className="tool-select-btn-active" 
                type="button"
                onClick={() => { setIsToolSelectPopoverOpen(true); }}
              >
                <div className="tool-icon-small tool-icon-main">
                  <img src={IconToolActive} alt="tool" />
                </div>
                <div className="tool-icons">
                  {allEnabledIcons.slice(0, 5).map((icon) => (
                    <div key={icon.id} className={`tool-icon-small ${icon.type === 'mcp' ? 'mcp-icon' : ''}`}>
                      {icon.type === 'connector' ? (
                        <img src={icon.iconUrl} alt={icon.name} className="connector-icon-small" />
                      ) : (
                        <Badge accent="normal" size="small" round>{icon.name}</Badge>
                      )}
                    </div>
                  ))}
                </div>
                {totalEnabledCount > 5 && (
                  <span className="tool-count-badge">+{totalEnabledCount - 5}</span>
                )}
                {showAttentionBadge && (
                  <Tooltip message={attentionMessage} className="tool-attention-tooltip-wrap">
                    <span className={attentionBadgeClass}>
                      <span className="attention-icon">!</span>
                    </span>
                  </Tooltip>
                )}
              </button>
            </div>
          )}
        </div>
      )}
      {/* 모바일 도구 선택 팝오버 - 독립적으로 렌더링 */}
      {!isDesktop && isToolSelectPopoverOpen && (
        <div className="chat-tool-select-overlay" onClick={() => { setIsToolSelectPopoverOpen(false); }}>
          <div className="ds-popover chat-tool-select-popover" data-state="show" onClick={(e) => { e.stopPropagation(); }}>
            <div className="chat-tool-select-content">
              <DropdownHeader title="도구" size="large">
                <button
                  className="tool-select-popover-detail-link"
                  onClick={() => {
                    setIsToolSelectPopoverOpen(false);
                    dispatch(showSettingDialog({ visible: true, type: 3 }));
                  }}
                  type="button"
                >
                  <Icon name="setting" />
                  <span>상세 보기</span>
                </button>
                <Button
                  className="ds-control-close"
                  control
                  textBlind
                  onClick={() => {
                    setIsToolSelectPopoverOpen(false);
                  }}
                >
                  닫기
                </Button>
              </DropdownHeader>
              <ToolSelectPopover
                isOpen={isToolSelectPopoverOpen}
                showHeader={false}
                localMcpTools={localMcpTools}
                isDesktop={false}
                // onToolToggle={(toolId, enabled) => {
                //   console.log('Tool toggled:', toolId, enabled);
                //   // void fetchEnabledConnectors();
                // }}
                onOpenSettings={() => {
                  setIsToolSelectPopoverOpen(false);
                  dispatch(showSettingDialog({ visible: true, type: 3 }));
                }}
              />
            </div>
          </div>
        </div>
      )}
      {agent.isInputFormVisible && agent.agentInputForm.length > 0 && (
        <AgentInputForm
          userInputForm={agent.agentInputForm}
          initialValues={agent.inputValues}
          onSubmit={handleAgentInputSubmit}
          onCancel={() => { agent.setInputFormVisible(false); }}
        />
      )}
      <Textarea
        ref={textAreaRef}
        selectedId={selectedId}
        placeholder={
          selectedId !== '' && conversations[selectedId].botResponseStatus
            ? '답변을 작성하고 있어요.'
            : `Works AI에게 무엇이든 물어보세요. ${isDesktop ? '‘@’ 입력 시 빠른 에이전트 선택이 가능해요.' : ''}`
        }
        value={value}
        setValue={setValue}
        isOpen={isPluginOpen}
        setSearchQuery={setSearchQuery}
        handleKeyDown={handleKeyDown}
        {...(!isDesktop && { onClick: scrollToBottom })} // 조건부로 onClick 속성 추가
      />
      <div className='chat-input-option'>
        {/* Mobile용 버튼 추가 */}
        <div className='option' ref={optionRef}>
          <Button
            className="chat-functions"
            icon={isOptionVisible ? 'close' : 'add'}
            control
            textBlind
            onClick={toggleOption}
          >
            추가기능
          </Button>
          <div
            className="input-option"
            data-state={isOptionVisible ? 'show' : 'hide'}
            onClick={() => {
              setIsOptionVisible(false);
            }}

          >
            {isDesktop ? (
              <div className="chat-attach" onDragOver={dragIn} onDragLeave={dragOut}>
                <TooltipButton message="문서를 첨부해 요약, 질문을 해 보세요." icon="attach" textBlind={false} onClick={addFiles} position={['right']}>
                  파일첨부
                </TooltipButton>
                <FileUploader
                  onFileUpload={(files: File[]) => {
                    setIsDragging(false);
                    fileUpload(files);
                  }}
                  className={isDragging ? 'is-show' : ''}
                />
              </div>
            ) : (
              <>
                <Button icon="attach" control onClick={addFiles}>
                  파일첨부
                </Button>
                <FileUploader onFileUpload={fileUpload} />
              </>
            )}
            {isDesktop ? (
              <div className="chat-template-menu">
                <TooltipButton
                  message="템플릿을 통해 질문을 구체화해 보세요."
                  icon="templete"
                  onClick={() => {
                    setTemplateState(!templateState);
                  }}
                  textBlind={false}
                  position={['right']}
                >
                  템플릿
                </TooltipButton>
                <Dialog
                  className="chat-template"
                  title="즐겨찾는 프롬프트 템플릿"
                  isOpen={templateState}
                  onClose={() => {
                    setTemplateState(false);
                  }}
                  closeOnOutSide={true}
                  size="large"
                >
                  {templateState && (
                    <>
                      <Stack className="dsx-align-end">
                        <Button
                          classOnly="chat-button-edit"
                          onClick={() => {
                            const options =
                              'location=no,menubar=no,toolbar=yes,scrollbars=no,resizable=yes,width=1600,height=800,left=200,top=200'; // 팝업 창의 크기와 위치 및 설정
                            window.open('/makers/template', '_blank', options);
                            setTemplateState(false);
                          }}
                        >
                          편집하기
                        </Button>
                      </Stack>
                      <ChatTemplateList setValue={setValue} />
                    </>
                  )}
                </Dialog>
              </div>
            ) : (
              <Dropdown
                className="chat-template"
                trigger={
                  <TooltipButton message="템플릿을 통해 질문을 구체화해 보세요." icon="templete" textBlind={false} position={['right']}>
                    템플릿
                  </TooltipButton>
                }
                isOpen={templateState}
                changeState={changedTemplateState}
              >
                {templateState && (
                  <div className="chat-code">
                    <DropdownHeader title="즐겨찾는 프롬프트 템플릿" size="large">
                      <Button
                        className="ds-control-close"
                        control
                        textBlind
                        onClick={() => {
                          setTemplateState(false);
                        }}
                      >
                        닫기
                      </Button>
                    </DropdownHeader>
                    <ChatTemplateList setValue={setValue} />
                  </div>
                )}
              </Dropdown>
            )}
            <div
              className="chat-code"
              onClick={() => {
                handleClick('\n```[언어이름] \n코드를 입력해주세요. \n```');
              }}
            >
              <TooltipButton message="궁금한 코드를 질문해 보세요." icon="code" textBlind={false} position={['right']}>
                코드
              </TooltipButton>
            </div>
            <hr className='dsx-hr' />
            {/* 에이전트 모드 토글 버튼 */}
            {!isDesktop && <div className={classNames('chat-agent-mode', { 'on': agentMode })}>
              <TooltipButton
                message={"에이전트를 선택하지 않아도 에이전트가 대답해요."}
                icon="agent-mode"
                onClick={toggleAgentMode}
                textBlind={false}
                position={['right']}
              >
                에이전트 모드
              </TooltipButton>
            </div>}
            {/* <PluginMenuBar /> */}
            <div className={classNames("chat-plugin", { 'dropdown': isDesktop })} ref={pluginPopoverRef}>
              <TooltipButton
                message="에이전트를 통해 더 정확한 답변을 받아보세요."
                icon="prompt"
                onClick={togglePluginDropdown}
                textBlind={false}
                position={['right']}
              >
                에이전트
              </TooltipButton>
            </div>
            {!isDesktop && (
              <div className="chat-tool-select">
                <TooltipButton 
                  message="도구를 선택하여 사용할 수 있어요." 
                  theme="inverse"
                  icon="settings" 
                  textBlind={false} 
                  position={['right']}
                  onClick={() => { setIsToolSelectPopoverOpen(true); }}
                >
                  도구
                </TooltipButton>
              </div>
            )}
            {/* <div className="chat-attach" onClick={() => documentFileRef.current?.click()}> */}
            {/* 웹검색은 별도 Tool로 제외 */}
            {selectedPlugin?.externalSource === ExternalSource.Aionu ? null : (
              <>
                {Object.values(webSearchTool).map((tool: WebSearch, index: number) => (
                  <Fragment key={index}>
                    {tool.isShowMenu && (
                      <div key={index} className={classNames('chat-websearch')}>

                        <TooltipButton
                          message={tool.description}
                          icon={tool.icon}
                          textBlind={false}
                          onClick={() => {
                            changedWebSearcSource(tool.value);
                            setwebSearchState(!webSearchState);
                          }}
                          position={['right']}>
                          {tool.label}
                        </TooltipButton>
                      </div>
                    )}
                  </Fragment>
                ))}
              </>
            )}
            {/* {window.electron?.isElectron && Object.entries(tools).length > 0 && <ToolList data={tools} />} */}
          </div>
        </div>
        {isDesktop && <div className={classNames('chat-agent-mode', { 'on': agentMode })}>
          <TooltipButton
            message={"에이전트를 선택하지 않아도 에이전트가 대답해요."}
            icon="agent-mode"
            onClick={toggleAgentMode}
            textBlind={false}
          >
            에이전트 모드
          </TooltipButton>
        </div>
        }
        {isDesktop && (
          <div className="chat-tool-select">
            <Dropdown
              className="chat-tool-select-dropdown"
              trigger={
                totalEnabledCount > 0 ? (
                  <Tooltip message={"도구를 선택하여 사용할 수 있어요."} position={['bottom', 'right']} theme="inverse">
                    <button className="tool-select-btn-active" type="button">
                      <div className="tool-icon-small tool-icon-main">
                        <img src={IconToolActive} alt="tool" />
                      </div>
                      <div className="tool-icons">
                        {allEnabledIcons.slice(0, 5).map((icon) => (
                          <div key={icon.id} className={`tool-icon-small ${icon.type === 'mcp' ? 'mcp-icon' : ''}`}>
                            {icon.type === 'connector' ? (
                              <img src={icon.iconUrl} alt={icon.name} className="connector-icon-small" />
                            ) : (
                              <Badge accent="normal" size="small" round>{icon.name}</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                      {totalEnabledCount > 5 && (
                        <span className="tool-count-badge">+{totalEnabledCount - 5}</span>
                      )}
                      {showAttentionBadge && (
                        <Tooltip message={attentionMessage} className="tool-attention-tooltip-wrap">
                          <span className={attentionBadgeClass}>
                            <span className="attention-icon">!</span>
                          </span>
                        </Tooltip>
                      )}
                    </button>
                  </Tooltip>
                ) : showAttentionBadge ? (
                  <Tooltip message={attentionMessage} className="tool-attention-tooltip-wrap">
                    <button className="tool-select-btn-attention" type="button">
                      <span className={attentionBadgeClass}>
                        <span className="attention-icon">!</span>
                      </span>
                    </button>
                  </Tooltip>
                ) : (
                  <TooltipButton
                    message={"도구를 선택하여 사용할 수 있어요."}
                    theme="inverse"
                    icon="settings"
                    textBlind={true}
                  >
                    도구 선택
                  </TooltipButton>
                )
              }
              isOpen={isToolSelectPopoverOpen}
              changeState={setIsToolSelectPopoverOpen}
              position="top"
              popoverClassName="tool-select-popover"
            >
              <ToolSelectPopover
                isOpen={isToolSelectPopoverOpen}
                localMcpTools={localMcpTools}
                isDesktop={true}
                // onToolToggle={(toolId, enabled) => {
                //   console.log('Tool toggled:', toolId, enabled);
                //   // 토글 후 새로고침
                //   // void fetchEnabledConnectors();
                // }}
                onOpenSettings={() => {
                  setIsToolSelectPopoverOpen(false);
                  dispatch(showSettingDialog({ visible: true, type: 3 }));
                }}
              />
            </Dropdown>
          </div>
        )}
        {/* MCP 서버 목록은 ToolSelectPopover에 통합됨 */}
      </div >
      <div className="enter-area">
        {isDesktop && (
          <ChatModelSelect
            className="chat-select"
            data={models}
            selectedName={
              selectedPlugin?.externalSource === ExternalSource.Aionu ? selectedPlugin.name : selectedModel.displayName
            }
            onSelect={changeSelection}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            disabled={selectedPlugin?.externalSource === ExternalSource.Aionu}
            message={messageContent}
          />
        )}

        {selectedId !== '' && conversations[selectedId].botResponseStatus ? (
          <Button
            className="chat-stop"
            icon={selectedId === '' ? 'send' : 'pause'}
            control
            textBlind
            onClick={() => {
              stopResponse();
            }}
          >
            답변 정지
          </Button>
        ) : (
          <Button
            className="chat-send"
            icon="send"
            control
            textBlind
            disabled={value.trim() === ''}
            onClick={() => {
              handleSubmit(value);
            }}
          >
            보내기
          </Button>
        )}
      </div>
    </div >
  );
};
